import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext"; // Import Pricing Context
import { useGuestChat } from "../hooks/useGuestChat";
import "./Navbar.css";

import {
  IconBook,
  IconCalculator,
  IconCalendarEvent,
  IconChartDots,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconCrown,
  IconFileText,
  IconGift,
  IconHeart,
  IconMessageCircle,
  IconPhone,
  IconReceipt,
  IconShoppingBag,
  IconSparkles,
  IconSun,
  IconUser,
  IconWallet,
} from "@tabler/icons-react";
import logoSvg from "../assets/logo.svg";
import AuthModal from "./AuthModal";
import CustomLanguageSelector from "./CustomLanguageSelector";
import NavbarProfileSelector from "./NavbarProfileSelector";
import SubscribeSheet from "./SubscribeSheet";
import {
  LANDING_PATHS,
  getLandingPath,
  normalisePath,
} from "../hooks/useLandingMode";

const Navbar: React.FC = () => {
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState<boolean>(false);
  const handleSignOutConfirm = () => {
    signOut();
    setIsSignOutModalOpen(false);
    closeAllMenus();
  };
  const {
    user,
    signOut,
    signInWithGoogle,
    planTier,
    walletBalance, // Changed from coinBalance
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    selectedProfile,
    setSelectedProfile,
    enrichedProfiles,
    currency,
    userCountry,
  } = useAuth();
  const { guestChatEnabled } = useGuestChat();

  // Remedies store is India-only. Require BOTH INR pricing AND a India geo
  // signal (userCountry, the geoapify-detected location used elsewhere) so a
  // non-Indian user whose currency_code is stale/defaulted to INR still never
  // sees the shop.
  const isIndia =
    currency === "INR" && (userCountry || "IN").toUpperCase() === "IN";

  const { formatPrice, prices, isLoading: isPricingLoading } = usePricing(); // Get formatter
  const navigate = useNavigate();
  const location = useLocation();

  // In the /market or /global experience, "Home" (logo + menu) returns there,
  // not to the main site.
  const auraMode = (() => {
    try {
      return sessionStorage.getItem("auraMode");
    } catch {
      return null;
    }
  })();
  /* "Home" on a campaign landing page means THAT page, not the main homepage.
     These are bought with ad spend and have no relationship to the homepage, so
     sending a visitor there takes them off the page we paid to put them on.
     Checked before market/global because a landing path is the more specific
     answer. */
  const landingHome =
    (LANDING_PATHS.includes(normalisePath(location.pathname))
      ? normalisePath(location.pathname)
      : null) ?? getLandingPath();

  const homeTo =
    landingHome ??
    (location.pathname === "/market" || auraMode === "market"
      ? "/market"
      : location.pathname === "/global" || auraMode === "global"
        ? "/global"
        : "/");

  /* Campaign landing pages, where the nav must not offer a way out.
     Blog, Calculators, the Free tools rows and Shop all lead somewhere the ad
     spend is not paying for, so they come off on these routes and nowhere else.

     /usa, /uae, /love and /horoscope are INTERNATIONAL pages, where Shop is
     worse than a distraction: remedies are India-only, so it advertises
     something that visitor cannot buy. The calculators are India-centric Vedic
     tools for the same reason.

     Trailing slash is stripped because Amplify 301s /india to /india/ and a
     bare === would miss the redirected form. */
  const isFocusedLanding = LANDING_PATHS.includes(
    normalisePath(location.pathname),
  );

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  /**
   * Which desktop dropdown is open. Only "learn" exists today, but this is a
   * value rather than a boolean so adding a second menu later cannot produce
   * the state where two are open at once.
   */
  type NavMenu = "learn" | "calc";
  const [openMenu, setOpenMenu] = useState<NavMenu | null>(null);
  const isLearnMoreOpen = openMenu === "learn";
  const isCalcOpen = openMenu === "calc";
  const setIsLearnMoreOpen = (v: boolean) => setOpenMenu(v ? "learn" : null);
  const toggleMenu = (m: NavMenu) =>
    setOpenMenu((cur) => (cur === m ? null : m));

  /* Hover to open on desktop. Gated on a real pointer: on a touch screen a tap
     fires mouseenter as well as click, which would open and immediately close
     the menu. Click stays as the keyboard and touch path. */
  const hoverTimer = useRef<number | null>(null);
  const canHover = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const openOnHover = (m: NavMenu) => () => {
    if (!canHover()) return;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    setOpenMenu(m);
  };
  /* Small delay so a diagonal move towards the menu, or a slip between two
     toggles, does not shut it mid-gesture. */
  const closeOnHover = () => {
    if (!canHover()) return;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setOpenMenu(null), 140);
  };

  useEffect(
    () => () => {
      if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    },
    []
  );
  const [isMobilePricingOpen, setIsMobilePricingOpen] =
    useState<boolean>(false);
  /* Which group is expanded in the mobile sheet. One at a time, so the sheet
     never grows into the same endless scroll it replaced. */
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);
  const toggleMobileGroup = (k: string) =>
    setMobileGroup((cur) => (cur === k ? null : k));
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  // "Subscription" opens the app's subscribe bottom sheet (not the wallet
  // screen) — app parity.
  const [subscribeOpen, setSubscribeOpen] = useState<boolean>(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  // Closes whichever desktop dropdown is open when you click outside the whole
  // nav group, and on Escape. `dropdownRef` now wraps the entire links group
  // rather than one menu, so moving between menus does not need extra handling.
  useEffect(() => {
    if (!openMenu) return;
    const handleDocClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [openMenu]);

  const closeAllMenus = () => {
    setIsMenuOpen(false);
    setIsMobilePricingOpen(false);
    setOpenMenu(null);
    setMobileGroup(null);
  };

  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target as Node)
      ) {
        const hamburgerButton = document.querySelector(".hamburger-menu");
        if (hamburgerButton && !hamburgerButton.contains(e.target as Node)) {
          closeAllMenus();
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const userAvatarUrl = user?.user_metadata?.avatar_url;
  const userName = user?.user_metadata?.full_name || "User";

  const getStatusText = () => {
    if (planTier === "monthly" || planTier === "yearly") {
      return "Premium Member";
    }
    return "Free Tier";
  };

  const handleAuthRedirect = (e: React.MouseEvent) => {
    if (!user) {
      e.preventDefault();
      closeAllMenus();
      setIsAuthModalOpen(true);
    } else {
      closeAllMenus();
    }
  };

  // Same as above, but for the plain "Chat" links: a non-Indian guest is let
  // through to /chat (the teaser — ask 1 question, blurred answer, then sign in).
  // Indian guests still get the login sheet, unchanged. NOT used by the Call
  // links (/chat?startCall=1) — a call needs an account and a birth chart.
  const handleChatLinkClick = (e: React.MouseEvent) => {
    if (!user && guestChatEnabled) {
      closeAllMenus();
      return; // let the <Link> navigate
    }
    handleAuthRedirect(e);
  };

  const phoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestPhoneOtp,
      verifyOtp: verifyPhoneOtp,
    }),
    [requestPhoneOtp, verifyPhoneOtp],
  );

  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );

  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: verifyMsg91Otp,
    }),
    [verifyMsg91Otp],
  );

  // Helper to safely render balance
  // A signed-in user with no wallet balance yet is a BRAND-NEW account: the
  // balance lands only once init-user-wallet has run, and that does a
  // server-side geo lookup first, so it takes a beat. Showing "..." there is the
  // very first thing a new user sees, and it reads as broken.
  //
  // What that function grants as the starting balance is exactly the price of
  // ONE chat message (their free first question) — read from the same
  // service_prices row that PricingContext already has client-side. So show that
  // number: it's the credit they're being given, and it doesn't change when the
  // real balance arrives a moment later.
  const freeStartingCredit = prices["chat_message"] || 0;
  const renderBalance = () => {
    if (walletBalance === null) {
      return freeStartingCredit > 0 ? formatPrice(freeStartingCredit) : "...";
    }
    if (isPricingLoading) return "...";
    return formatPrice(walletBalance);
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSignOutModalOpen) {
        setIsSignOutModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSignOutModalOpen]);

  const signOutModalRef = useRef<HTMLDivElement | null>(null);
  const handleOutsideClick = (e: React.MouseEvent) => {
    // If the user clicks the overlay (the background), close the modal
    if (
      signOutModalRef.current &&
      !signOutModalRef.current.contains(e.target as Node)
    ) {
      setIsSignOutModalOpen(false);
    }
  };
  return (
    <>
      <nav className="navbar-container">
        <div className="navbar-group-left">
          <Link to={homeTo} className="navbar-logo">
            <img src={logoSvg} alt="AstroAura Logo" className="logo-svg" />
            <div className="logo-text">Aura AI</div>
          </Link>
        </div>

        <div className="nav-links-group" ref={dropdownRef}>
          <Link
            to="/reports#ai"
            className="nav-link"
            onClick={handleAuthRedirect}
          >
            Reports
          </Link>
          <Link
            to="/reports#tables"
            className="nav-link"
            onClick={handleAuthRedirect}
          >
            Birth Chart
          </Link>
          <Link to="/chat" className="nav-link" onClick={handleChatLinkClick}>
            Chat
          </Link>
          <Link
            to="/chat?startCall=1"
            className="nav-link"
            onClick={handleAuthRedirect}
          >
            Call
          </Link>

          {/* SHOP. Points at the Shopify storefront, which is where the
              catalogue actually lives, NOT the internal /store page.

              Deliberately NOT gated on isIndia: the old gate needed BOTH
              currency INR and a geoapify country of IN, so it stayed hidden
              whenever the geo probe had not resolved, which is why it kept not
              appearing at all. A link out to the store is harmless to show
              anyone. Hidden on /india only, for the same reason as Blog.

              External, so a plain anchor with rel noopener, not a router
              Link. */}
          {!isFocusedLanding && (
            <a
              href="https://astroaura.market/"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link"
              onClick={closeAllMenus}
            >
              Shop
            </a>
          )}

          {/* BLOG promoted to top level, out of the Learn More dropdown.
              Hidden on /india: that page is bought traffic with one job, and
              Blog and Calculators are the two links that lead away from it. */}
          {!isFocusedLanding && (
            <Link to="/blog" className="nav-link" onClick={closeAllMenus}>
              Blog
            </Link>
          )}

          {/* CALCULATORS. Its own menu rather than another row inside Learn
              More: these are indexable tool pages and this is where they grow
              as each one ships. Plain Links, same tab, no auth. */}
          {!isFocusedLanding && (
          <div
            className="nav-dropdown"
            onMouseEnter={openOnHover("calc")}
            onMouseLeave={closeOnHover}
          >
            <button
              className="nav-dropdown-toggle"
              onClick={() => toggleMenu("calc")}
              aria-haspopup="true"
              aria-expanded={isCalcOpen}
            >
              Calculators
              <IconChevronDown
                size={16}
                className={`dropdown-arrow ${isCalcOpen ? "open" : ""}`}
              />
            </button>
            {isCalcOpen && (
              <div className="nav-dropdown-menu">
                <Link
                  to="/free-astrology"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  All Free Astrology Tools
                </Link>
                <Link
                  to="/manglik-calculator"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Manglik Calculator
                </Link>
                <Link
                  to="/kaal-sarp-dosha"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Kaal Sarp Dosha
                </Link>
                <Link
                  to="/sade-sati-calculator"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Sade Sati Calculator
                </Link>
                <Link
                  to="/moon-sign-calculator"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Moon Sign Calculator
                </Link>
                <Link
                  to="/nakshatra-calculator"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Nakshatra Calculator
                </Link>
                <Link
                  to="/rising-sign-calculator"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Rising Sign Calculator
                </Link>
                <Link
                  to="/mahadasha-calculator"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Mahadasha Calculator
                </Link>
                <Link
                  to="/free-kundli"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Free Kundli
                </Link>
                <Link
                  to="/kundli-matching"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Kundli Matching
                </Link>

                {/* Panchang and Muhurat read today's sky rather than a birth
                    chart, so they sit under their own heading instead of being
                    mixed in with the ten chart calculators. Both were
                    prerendered and indexable with no nav link at all. */}
                <p className="nav-dropdown-heading">Daily almanac</p>
                <Link
                  to="/daily-horoscope"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Daily Horoscope
                </Link>
                <Link
                  to="/panchang"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Today&apos;s Panchang
                </Link>
                <Link
                  to="/muhurat"
                  className="nav-dropdown-item"
                  onClick={closeAllMenus}
                >
                  Muhurat Finder
                </Link>
              </div>
            )}
          </div>
          )}

          <div
            className="nav-dropdown"
            onMouseEnter={openOnHover("learn")}
            onMouseLeave={closeOnHover}
          >
            <button
              className="nav-dropdown-toggle"
              onClick={() => toggleMenu("learn")}
              aria-haspopup="true"
              aria-expanded={isLearnMoreOpen}
            >
              Learn More
              <IconChevronDown
                size={16}
                className={`dropdown-arrow ${isLearnMoreOpen ? "open" : ""}`}
              />
            </button>
            {isLearnMoreOpen && (
              <div className="nav-dropdown-menu">
                {/* FREE TOOLS.
                    /free-kundli, /kundli-matching and /daily-horoscope are
                    indexed pages with real content that nothing in the navbar
                    linked to. They were reachable only from the footer, so they
                    got almost no internal link weight. Plain crawlable Links,
                    no auth needed.

                    "Talk to an Astrologer" is the exception: it points at the
                    call feature (/chat?startCall=1), not the landing page. A
                    phone-labelled nav item that opens a marketing page is a
                    dead end. /talk-to-astrologer keeps its internal links from
                    the footer and two blog posts. */}
                <Link
                  to="/chat?startCall=1"
                  className="nav-dropdown-item"
                  onClick={handleAuthRedirect}
                >
                  Talk to an Astrologer
                </Link>

                <hr className="nav-dropdown-divider" />

                <Link
                  to="/pricing"
                  className="nav-dropdown-item"
                  onClick={() => setIsLearnMoreOpen(false)}
                >
                  Pricing Details
                </Link>
                <Link
                  to="/how-it-works"
                  className="nav-dropdown-item"
                  onClick={() => setIsLearnMoreOpen(false)}
                >
                  How it works
                </Link>
                {/* Blog lives in the top-level bar now, so it is not
                    repeated here. */}

                <hr className="nav-dropdown-divider" />
                <span className="nav-dropdown-label">Your account</span>

                {/* These three used to be wrapped in {user && ...}, so a
                    logged-out visitor saw a three-item dropdown and no sign of
                    them at all. They are always shown now: clicking one while
                    signed out opens the auth sheet, which is the same
                    behaviour as the Reports and Call links in the bar. */}
                <Link
                  to="/orders"
                  className="nav-dropdown-item"
                  onClick={handleAuthRedirect}
                >
                  Orders &amp; billing
                </Link>
                <button
                  type="button"
                  className="nav-dropdown-item"
                  style={{
                    background: "none",
                    border: "none",
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setIsLearnMoreOpen(false);
                    if (!user) {
                      setIsAuthModalOpen(true);
                      return;
                    }
                    setSubscribeOpen(true);
                  }}
                >
                  Subscription
                </button>
                <Link
                  to="/referral"
                  className="nav-dropdown-item"
                  onClick={handleAuthRedirect}
                >
                  Refer &amp; Earn
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="navbar-group-right">


          {user ? (
            <>
              <div className="user-actions-group">
                <button
                  type="button"
                  className="wallet-pill"
                  onClick={() => navigate("/wallet")}
                  aria-label="Open Wallet"
                >
                  <IconWallet size={16} />
                  {/* UPDATED: Uses formatPrice now */}
                  <span className="wallet-text">{renderBalance()}</span>
                </button>

                {/* REPLACED: Static Profile Info with Selector */}
                <NavbarProfileSelector
                  profiles={enrichedProfiles}
                  selectedProfileId={selectedProfile?.id || null}
                  onProfileSelect={setSelectedProfile}
                  onAddProfileClick={() => navigate("/profiles/new")}
                />

                {/* RESTORED: Profile Link for Desktop */}
                <Link
                  to="/profiles"
                  className="nav-profile-link"
                  title="My Profile"
                >
                  <div className="nav-avatar">
                    {userAvatarUrl ? (
                      <img src={userAvatarUrl} alt="Profile" />
                    ) : (
                      <IconUser size={20} />
                    )}
                  </div>
                </Link>
              </div>

              {/* No GoogleTranslateWidget here: App.tsx mounts the only one.
                  A second copy in this logged-in branch made two
                  #google_translate_element hosts; Google built into this one,
                  and signing out unmounted it along with Google's <select>,
                  so language switches silently did nothing until a reload. */}
              <div className="language-tools-group">
                <CustomLanguageSelector />
              </div>
            </>
          ) : (
            <div className="language-tools-group">
              <CustomLanguageSelector />
            </div>
          )}

          {/* PRIMARY CTA.
              Uses .nav-button-gradient-cta, which was already defined in
              Navbar.css and used nowhere: an existing brand style rather than
              a new one invented for this.

              The label is the product's own phrase. "Ask Aura AI" is already
              the /market H1 and the use-cases heading, so the bar now says
              what the rest of the site says. Deliberately one plain label: no
              divider, no second line, nothing borrowed from another site's
              bar.

              Logged-out only. A signed-in user already carries a wallet pill,
              a name dropdown and a profile button on the right. */}
          {!user && <span className="nav-cta-divider" aria-hidden="true" />}
          {!user && (
            <button
              type="button"
              className="nav-button-gradient-cta nav-cta-compact"
              onClick={() => {
                closeAllMenus();
                if (guestChatEnabled) navigate("/chat");
                else setIsAuthModalOpen(true);
              }}
            >
              Ask Aura AI
            </button>
          )}
        </div>

        {/* --- Mobile Controls --- */}

        <div className="mobile-controls">
          {user && (
            <button
              type="button"
              className="wallet-pill mobile-wallet-pill"
              onClick={() => navigate("/wallet")}
              aria-label="Open Wallet"
            >
              <IconWallet size={16} />
              {/* UPDATED: Uses formatPrice now */}
              <span className="wallet-text">{renderBalance()}</span>
            </button>
          )}
          {location.pathname !== "/chat" && (
            <Link
              to="/chat"
              className={`mobile-chat-pill ${user ? "loggedin" : "guest"}`}
              onClick={handleChatLinkClick}
            >
              <IconMessageCircle size={16} className="chat-pill-icon" />
              <span>Chat</span>
            </Link>
          )}

          {user && (
            <div style={{ marginLeft: "10px", marginRight: "10px" }}>
              <NavbarProfileSelector
                profiles={enrichedProfiles}
                selectedProfileId={selectedProfile?.id || null}
                onProfileSelect={setSelectedProfile}
                onAddProfileClick={() => navigate("/profiles/new")}
              />
            </div>
          )}

          {/* Three bars rather than one static path, so the icon can fold into
              a cross while the sheet is open. It is the only close control, so
              its state has to read as "this closes it". */}
          <button
            className={`hamburger-menu ${isMenuOpen ? "is-open" : ""}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            <span className="hamburger-box">
              <span className="hamburger-bar" />
              <span className="hamburger-bar" />
              <span className="hamburger-bar" />
            </span>
          </button>
        </div>

        {isMenuOpen && (
          <>
            <div className="mobile-menu-backdrop" onClick={closeAllMenus}></div>
            <div className="mobile-menu" ref={mobileMenuRef}>
              {/* Sticky head. The sheet scrolls between this and the footer so
                  the brand and the way out are always reachable. */}
              <div className="mmenu-head">
                <Link
                  to={homeTo}
                  className="mmenu-brand"
                  onClick={closeAllMenus}
                >
                  <img src={logoSvg} alt="" className="mmenu-brand-logo" />
                  <span>Aura AI</span>
                </Link>
              </div>

              <div className="mmenu-scroll">
                {user ? (
                  <Link
                    to="/profiles"
                    className="mmenu-identity"
                    onClick={closeAllMenus}
                  >
                    <span className="profile-avatar">
                      {userAvatarUrl ? (
                        <img
                          src={userAvatarUrl}
                          alt={userName}
                          className="profile-avatar-image"
                        />
                      ) : (
                        <span className="profile-avatar-icon">
                          <IconUser size={20} />
                        </span>
                      )}
                    </span>
                    <span className="user-texts">
                      <span className="user-name">{userName}</span>
                      <span className="user-status">{getStatusText()}</span>
                    </span>
                    <IconChevronRight size={18} className="mmenu-chev" />
                  </Link>
                ) : (
                  <div className="mmenu-signin">
                    <button
                      type="button"
                      className="mmenu-cta"
                      onClick={() => {
                        closeAllMenus();
                        setIsAuthModalOpen(true);
                      }}
                    >
                      <IconUser size={18} />
                      Sign in / Sign up
                    </button>
                    <p className="mmenu-cta-sub">Your first question is free</p>
                  </div>
                )}

                <p className="mmenu-label">Consult</p>
                <div className="mmenu-card">
                  <Link
                    to="/chat"
                    className="mmenu-row"
                    onClick={handleChatLinkClick}
                  >
                    <span className="mmenu-ico">
                      <IconMessageCircle size={18} />
                    </span>
                    <span className="mmenu-row-label">Chat</span>
                    <IconChevronRight size={16} className="mmenu-chev" />
                  </Link>
                  <Link
                    to="/reports#ai"
                    className="mmenu-row"
                    onClick={handleAuthRedirect}
                  >
                    <span className="mmenu-ico">
                      <IconFileText size={18} />
                    </span>
                    <span className="mmenu-row-label">Reports</span>
                    <IconChevronRight size={16} className="mmenu-chev" />
                  </Link>
                  <Link
                    to="/reports#tables"
                    className="mmenu-row"
                    onClick={handleAuthRedirect}
                  >
                    <span className="mmenu-ico">
                      <IconChartDots size={18} />
                    </span>
                    <span className="mmenu-row-label">Birth Chart</span>
                    <IconChevronRight size={16} className="mmenu-chev" />
                  </Link>
                  <Link
                    to="/chat?startCall=1"
                    className="mmenu-row"
                    onClick={handleAuthRedirect}
                  >
                    <span className="mmenu-ico">
                      <IconPhone size={18} />
                    </span>
                    <span className="mmenu-row-label">
                      Talk to an Astrologer
                    </span>
                    <IconChevronRight size={16} className="mmenu-chev" />
                  </Link>
                </div>

                {/* FREE TOOLS. The whole block goes on /india, not just the
                    Calculators row: Daily Horoscope, Panchang and Muhurat are
                    tool pages too, and leaving them behind meant the sheet
                    still offered three ways off a page bought to have one. */}
                {!isFocusedLanding && (
                <>
                <p className="mmenu-label">Free tools</p>
                <div className="mmenu-card">
                  {/* Ten calculators behind one row. Listing them flat is what
                      turned this sheet into an endless scroll. */}
                  <>
                  <button
                    type="button"
                    className={`mmenu-row mmenu-row-toggle ${
                      mobileGroup === "calc" ? "is-open" : ""
                    }`}
                    onClick={() => toggleMobileGroup("calc")}
                    aria-expanded={mobileGroup === "calc"}
                  >
                    <span className="mmenu-ico">
                      <IconCalculator size={18} />
                    </span>
                    <span className="mmenu-row-label">Calculators</span>
                    <span className="mmenu-count">10</span>
                    <IconChevronDown size={16} className="mmenu-chev" />
                  </button>
                  {mobileGroup === "calc" && (
                    <div className="mmenu-sub">
                      {[
                        ["/manglik-calculator", "Manglik Calculator"],
                        ["/kaal-sarp-dosha", "Kaal Sarp Dosha"],
                        ["/sade-sati-calculator", "Sade Sati Calculator"],
                        ["/moon-sign-calculator", "Moon Sign Calculator"],
                        ["/nakshatra-calculator", "Nakshatra Calculator"],
                        ["/rising-sign-calculator", "Rising Sign Calculator"],
                        ["/mahadasha-calculator", "Mahadasha Calculator"],
                        ["/free-kundli", "Free Kundli"],
                        ["/kundli-matching", "Kundli Matching"],
                      ].map(([to, label]) => (
                        <Link
                          key={to}
                          to={to}
                          className="mmenu-sub-link"
                          onClick={closeAllMenus}
                        >
                          {label}
                        </Link>
                      ))}
                    </div>
                  )}
                  </>
                  <Link
                    to="/daily-horoscope"
                    className="mmenu-row"
                    onClick={closeAllMenus}
                  >
                    <span className="mmenu-ico">
                      <IconSun size={18} />
                    </span>
                    <span className="mmenu-row-label">Daily Horoscope</span>
                    <IconChevronRight size={16} className="mmenu-chev" />
                  </Link>
                  <Link
                    to="/panchang"
                    className="mmenu-row"
                    onClick={closeAllMenus}
                  >
                    <span className="mmenu-ico">
                      <IconCalendarEvent size={18} />
                    </span>
                    <span className="mmenu-row-label">Panchang</span>
                    <IconChevronRight size={16} className="mmenu-chev" />
                  </Link>
                  <Link
                    to="/muhurat"
                    className="mmenu-row"
                    onClick={closeAllMenus}
                  >
                    <span className="mmenu-ico">
                      <IconClock size={18} />
                    </span>
                    <span className="mmenu-row-label">Muhurat</span>
                    <IconChevronRight size={16} className="mmenu-chev" />
                  </Link>
                </div>
                </>
                )}

                {/* MORE holds only Blog and Shop, and both come off on the
                    campaign pages. The label and the card have to go with
                    them, or the sheet shows a "More" heading above an empty
                    box. The Account block below is NOT part of this: it is
                    gated on being signed in, never on the route, so Wallet,
                    Orders & billing, Subscription and Refer & Earn stay
                    exactly where they are on every page. */}
                {!isFocusedLanding && (
                <>
                <p className="mmenu-label">More</p>
                <div className="mmenu-card">
                  {(
                    <Link
                      to="/blog"
                      className="mmenu-row"
                      onClick={closeAllMenus}
                    >
                      <span className="mmenu-ico">
                        <IconBook size={18} />
                      </span>
                      <span className="mmenu-row-label">Blog</span>
                      <IconChevronRight size={16} className="mmenu-chev" />
                    </Link>
                  )}

                  {/* Shop: same target as the desktop bar, the Shopify
                      storefront rather than the internal /store page, and not
                      behind the isIndia gate. External, so a plain anchor. */}
                  {(
                    <a
                      href="https://astroaura.market/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mmenu-row"
                      onClick={closeAllMenus}
                    >
                      <span className="mmenu-ico">
                        <IconShoppingBag size={18} />
                      </span>
                      <span className="mmenu-row-label">Shop</span>
                      <IconChevronRight size={16} className="mmenu-chev" />
                    </a>
                  )}
                </div>
                </>
                )}

                {user && (
                  <>
                    <p className="mmenu-label">Account</p>
                    <div className="mmenu-card">
                      <Link
                        to="/wallet"
                        className="mmenu-row"
                        onClick={closeAllMenus}
                      >
                        <span className="mmenu-ico">
                          <IconWallet size={18} />
                        </span>
                        <span className="mmenu-row-label">Wallet</span>
                        <IconChevronRight size={16} className="mmenu-chev" />
                      </Link>
                      <Link
                        to="/orders"
                        className="mmenu-row"
                        onClick={closeAllMenus}
                      >
                        <span className="mmenu-ico">
                          <IconReceipt size={18} />
                        </span>
                        <span className="mmenu-row-label">
                          Orders &amp; billing
                        </span>
                        <IconChevronRight size={16} className="mmenu-chev" />
                      </Link>
                      <button
                        type="button"
                        className="mmenu-row"
                        onClick={() => {
                          closeAllMenus();
                          setSubscribeOpen(true);
                        }}
                      >
                        <span className="mmenu-ico">
                          <IconCrown size={18} />
                        </span>
                        <span className="mmenu-row-label">Subscription</span>
                        <IconChevronRight size={16} className="mmenu-chev" />
                      </button>
                      <Link
                        to="/referral"
                        className="mmenu-row"
                        onClick={closeAllMenus}
                      >
                        <span className="mmenu-ico">
                          <IconGift size={18} />
                        </span>
                        <span className="mmenu-row-label">Refer &amp; Earn</span>
                        <IconChevronRight size={16} className="mmenu-chev" />
                      </Link>
                    </div>
                  </>
                )}
              </div>

              {/* Sticky foot: language always reachable without scrolling the
                  whole sheet, sign out last so it is never a mis-tap. */}
              <div className="mmenu-foot">
                <div className="mobile-language-selector">
                  <CustomLanguageSelector />
                </div>
                {user ? (
                  <button
                    type="button"
                    className="mmenu-signout"
                    onClick={() => {
                      setIsSignOutModalOpen(true);
                      closeAllMenus();
                    }}
                  >
                    Sign Out
                  </button>
                ) : null}
              </div>
            </div>
          </>
        )}
      </nav>
      {/* --- Sign Out Confirmation Modal --- */}
      {isSignOutModalOpen && (
        <div
          className="modal-overlay"
          onClick={handleOutsideClick}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="confirmation-modal"
            ref={signOutModalRef}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the box
          >
            <h3>Sign Out</h3>
            <p>Are you sure you want to sign out?</p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setIsSignOutModalOpen(false)}
              >
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleSignOutConfirm}>
                Yes, Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />

      {/* App's subscribe bottom sheet — opened from the "Subscription" menu
          item instead of routing to the wallet/management screen. */}
      <SubscribeSheet
        isOpen={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        source="navbar"
      />
    </>
  );
};

export default Navbar;
